// FLAGS: identity, personal-data
// The app's whole session lifecycle: registration, password/SSO/guest login, profile fetch,
// access-token refresh and logout. The only writer of the `user` sessionStorage entry.
import { useEffect } from "react";
import axios from "axios";
import { TypedUseSelectorHook, useDispatch, useSelector } from "react-redux";
import { apiPaths } from "@src/utils/constants";
import { trackAccountCreated } from "@src/utils/tracking/registration";
import { queryClient } from "@src/utils/queryClient";
import { EmployerSso, EmployerSsoRequest } from "@src/models/EmployerSso";
import {
  setAuthenticatedUserContext,
  clearAuthenticatedUserContext,
} from "@src/appInsights/AppInsights";
import { RootState, UserType } from "@src/reducers/reduxState";
import { posthogService } from "@src/services/posthog.service";
import { customerioService } from "@src/services/customerio.service";
import {
  RequestUpdateUserRequest,
  ResponseExternalLoginRegisterCodeFlowResponse,
  ResponseUserResponse,
} from "@src/api/v2/data-contracts";
import {
  RequestRegisterGuestUserRequest,
  RequestRegisterUserRequest,
} from "@src/api/v1/data-contracts";
import { refreshUserSessionId } from "@src/utils/userSessionId";
import { signalLoginSuccess } from "@src/utils/gtm-login-signal";
import localStorageService from "./localStorage.service";
import sessionStorageService from "./sessionStorage.service";
import {
  clearDirectSession,
  getDirectSession,
  getPhaseTwoEndSessionUrl,
} from "./phaseTwoDirectSession";
import { sendRefreshTokenEvent } from "./event.service";

const STORAGE_KEYS = {
  retryLogin: "retryLogin",
};

const getUserFromStorage = () =>
  JSON.parse(sessionStorage.getItem("user") as string);

// FLAGS: identity
// The only path that clears the Application Insights authenticated user id. `logout` does not call it,
// so a sign-out leaves telemetry stamped with the previous user for the rest of the page's life.
const clearUserFromStorage = () => {
  sessionStorage.removeItem("user");
  localStorage.removeItem(STORAGE_KEYS.retryLogin);
  clearAuthenticatedUserContext();
};

const setRetryLoginToStorage = () => {
  localStorage.setItem(
    STORAGE_KEYS.retryLogin,
    JSON.stringify({
      retryLogin: true,
    }),
  );
};

const getShouldRetryLoginFromStorage = () => {
  const retryLogin = localStorage.getItem(STORAGE_KEYS.retryLogin);
  if (retryLogin) {
    const { retryLogin: shouldRetryLogin } = JSON.parse(retryLogin);
    return shouldRetryLogin;
  }
  return false;
};

// FLAGS: identity, personal-data
// Writes the profile to sessionStorage and stamps `user.id` onto Application Insights. `login` calls it
// with only the email before the request is made, so a rejected attempt still leaves an email behind —
// and `getEmailDomain` then attaches its domain to every tracked exception.
const saveUserToStorage = (user) => {
  if (user) {
    sessionStorage.setItem("user", JSON.stringify(user));
    setRetryLoginToStorage();
    setAuthenticatedUserContext(user.id);
  } else {
    clearUserFromStorage();
  }
};

// FLAGS: identity
// The refresh the axios interceptor drives. The new cookie arrives on the response; nothing here reads
// or stores a token. A rejection here is what makes the interceptor sign the user out instead of retry.
const refreshAccessToken = async (token?: string) => {
  await axios.post(apiPaths.refreshToken, {
    token,
  });
  const user = getUserFromStorage();
  if (user?.id) {
    sendRefreshTokenEvent();
  }
};

const registerBookingUser = async (
  firstName,
  lastName,
  email,
  yearOfBirth,
  employerId,
  phoneNumber,
  termsAndConditions,
  medicalDataProcessing,
  isPartner,
  token,
  recaptchaToken,
) => {
  const { data } = await axios.post(
    `${apiPaths.registerGuestUserAfterBooking}?token=${token}&email=${email}`,
    {
      firstName,
      lastName,
      email,
      yearOfBirth,
      employerId,
      phoneNumber,
      acceptedTermsAndConditions: termsAndConditions,
      acceptedMedicalDataProcessing: medicalDataProcessing,
      isPartner,
      recaptchaToken,
    },
  );

  return data;
};

export interface RegisterGuestUserPayload
  extends RequestRegisterGuestUserRequest {
  isMedicalCheckin?: boolean;
  email: string;
}

const registerGuestUser = async ({
  yearOfBirth = null,
  employerId = null,
  phoneNumber = null,
  acceptedTermsAndConditions = false,
  acceptedMedicalDataProcessing = false,
  isMedicalCheckin = false,
  isPartner = false,
  ...restOfPayload
}: RegisterGuestUserPayload) => {
  const url = `${apiPaths.registerGuestUserAfterCheckIn}?isMedicalCheckIn=${isMedicalCheckin}`;

  const { data } = await axios.post(url, {
    ...restOfPayload,
    yearOfBirth,
    employerId,
    phoneNumber,
    acceptedTermsAndConditions,
    acceptedMedicalDataProcessing,
    isPartner,
  });
  return data;
};

export interface RegisterUserPayload extends RequestRegisterUserRequest {
  baseUrl?: string;
  email: string;
}

// FLAGS: personal-data
// Posts the registration payload — name, email, year of birth, password — and persists the returned
// signature to `localStorage`. Its Redux caller overwrites that key immediately with a different shape;
// see MODULE.md, *Gotchas*.
const registerUser = async ({
  redirectUri = undefined,
  focusThemes = [],
  baseUrl,
  ...restOfPayload
}: RegisterUserPayload) => {
  const { data } = await axios.post(
    apiPaths.registerUser,
    Object.fromEntries(
      Object.entries({
        ...restOfPayload,
        redirectUri,
        focusThemes,
      }).filter(([, v]) => v != null),
    ),
    { baseURL: baseUrl || undefined },
  );

  localStorageService.setUnconfirmedDataSignature(data.signature);
  return data;
};

export interface UpdateUserPayload
  extends Omit<RequestUpdateUserRequest, "acceptedMedicalDataProcessing"> {
  firstName: string;
  lastName: string;
  email: string;
  yearOfBirth: number;
  isManager: boolean;
  acceptedMedicalDataProcessing?: boolean;

  baseUrl?: string;
}
const updateUser = async ({
  isPartner = false,
  baseUrl,
  ...restOfPayload
}: UpdateUserPayload) => {
  const { data } = await axios.put(
    apiPaths.updateUser,
    Object.fromEntries(
      Object.entries({
        ...restOfPayload,
        isPartner,
      }).filter(([, v]) => v != null),
    ),
    { baseURL: baseUrl || undefined },
  );

  saveUserToStorage(data);
  return data;
};

export const setFocusThemes = async (focusThemes: string[]): Promise<void> => {
  const url = apiPaths.setUserFocusedThemes;
  try {
    await axios.post(url, focusThemes);
  } catch (error: any) {
    throw new Error(
      `Failed to set focus themes (status: ${error?.response?.status ?? "unknown"})`,
    );
  }
};

// FLAGS: silent-failure
// A 401 resolves as `null`, so "not signed in" and "the profile call failed" are indistinguishable to
// the caller; an error carrying no `response` throws a TypeError on `error.response.status` instead.
export const getUserProfile = async (baseUrl: string | null = null) => {
  try {
    const { data } = await axios.get<ResponseUserResponse>(apiPaths.profile, {
      baseURL: baseUrl || undefined,
    });

    saveUserToStorage(data);
    return data;
  } catch (error) {
    if (error.response.status === 401) {
      return null;
    }
    throw new Error(error.response.data.message);
  }
};

// FLAGS: silent-failure
// Resolves `undefined` on any success status that is not exactly 200, so email confirmation reports
// neither success nor failure and the caller cannot tell that apart from a verified account.
// Verify User Email Api call
const verifyUserAccount = async (code, email) => {
  try {
    const response = await axios.put(
      `${apiPaths.confirmUser}?token=${code}&email=${email}`,
      {
        signature: JSON.parse(
          localStorageService.getUnconfirmedDataSignature() as string,
        )?.signature,
      },
    );
    if (response.status === 200) {
      return await getUserProfile();
    }
  } catch (error) {
    return Promise.reject(error);
  }
  return undefined;
};

// Resend Email Activation Link Api call
const resendVerificationEmail = async (email: string, redirectUri?: string) => {
  const { data } = await axios.post(
    `${apiPaths.sendVerificationEmail}?email=${email}${redirectUri ? `&redirectUri=${redirectUri}` : ""}`,
  );
  return data;
};

// Send forgot password Access Link Api call
const sendForgotPasswordAccessMail = async (
  email: string,
  recaptchaToken: string,
  redirectUrl: string | undefined = undefined,
) => {
  const encodedURI = redirectUrl ? encodeURIComponent(redirectUrl) : "";

  const { data } = await axios.post(apiPaths.sendPasswordResetEmail, {
    email,
    recaptchaToken,
    encodedURI,
  });
  return data;
};

// FLAGS: identity
// Completes a password reset from a code carried in the URL. Code validity, expiry and ownership are the
// API's to decide — nothing is checked here.
// Update User Password Api Call
const updateUserPassword = async (
  code: string,
  userId: string,
  password: string,
  recaptchaToken: string,
) => {
  await axios.put(apiPaths.resetPassword, {
    code,
    userId,
    password,
    recaptchaToken,
  });
};

// FLAGS: identity
// Establishes a session from an SSO id token. It and the four functions below it — the code-flow twin,
// both sign-up variants and the guest login — share one shape: return early while
// `twoFactorVerification === "pending"`, otherwise refresh the session id and fetch the profile. A path
// that skips that early return would hand a half-authenticated user a signed-in profile.
const getExternalUser = async (
  idToken: string,
  provider: string,
  baseUrl: string | null = null,
) => {
  const { data } =
    await axios.post<ResponseExternalLoginRegisterCodeFlowResponse>(
      apiPaths.externalLogin,
      {
        idToken,
        provider,
      },
      {
        baseURL: baseUrl || undefined,
      },
    );
  if (data.twoFactorVerification === "pending") {
    refreshUserSessionId();
    return data;
  }
  refreshUserSessionId();
  return getUserProfile();
};

const getExternalUserCodeFlow = async (
  code,
  provider,
  baseUrl: string | null = null,
) => {
  let domain = window.location.hostname;
  if (window.location.port && window.location.port !== "443") {
    domain = `${domain}:${window.location.port}`;
  }
  const { data } = await axios.post(
    apiPaths.loginExternalCodeFlow,
    {
      code,
      provider,
      domain,
    },
    {
      baseURL: baseUrl || undefined,
    },
  );
  if (data?.twoFactorVerification === "pending") {
    refreshUserSessionId();
    return data;
  }
  refreshUserSessionId();
  return getUserProfile();
};

const signUpExternalUser = async (idToken, provider) => {
  const { data } = await axios.post(apiPaths.externalSignUp, {
    idToken,
    provider,
  });
  // Reported here, not after the branch below: a 2FA-enabled account returns early, and waiting
  // would drop every SSO sign-up that has it. The endpoint is sign-up only — the login paths are
  // getExternalUser / getExternalUserCodeFlow — so this cannot fire for a returning user.
  trackAccountCreated();
  if (data?.twoFactorVerification === "pending") {
    refreshUserSessionId();
    return data;
  }
  refreshUserSessionId();
  return getUserProfile();
};

const signUpExternalUserCodeFlow = async (
  code,
  provider,
  baseUrl: string | null = null,
) => {
  try {
    let domain = window.location.hostname;
    if (window.location.port && window.location.port !== "443") {
      domain = `${domain}:${window.location.port}`;
    }
    const { data } = await axios.post(
      apiPaths.externalSignUpCodeFlow,
      {
        code,
        provider,
        domain,
      },
      {
        baseURL: baseUrl || undefined,
      },
    );
    // Reported here, not after the branch below: a 2FA-enabled account returns early, and waiting
    // would drop every SSO sign-up that has it. The endpoint is sign-up only — the login paths are
    // getExternalUser / getExternalUserCodeFlow — so this cannot fire for a returning user.
    trackAccountCreated();
    if (data?.twoFactorVerification === "pending") {
      refreshUserSessionId();
      return data;
    }
    refreshUserSessionId();
    return await getUserProfile();
  } catch (error) {
    return Promise.reject(error);
  }
};

const getGuestUser = async (token: string, email: string) => {
  const { data } =
    await axios.get<ResponseExternalLoginRegisterCodeFlowResponse>(
      `${apiPaths.guestLogin}?token=${token}&email=${email}`,
    );
  if (data?.twoFactorVerification === "pending") {
    refreshUserSessionId();
    return data;
  }
  refreshUserSessionId();
  return getUserProfile();
};

const getEmployerSso = async (employerSsoRequest: EmployerSsoRequest) => {
  const { data } = await axios.post<EmployerSso>(
    apiPaths.companySsoSupport,
    employerSsoRequest,
  );
  return data;
};

// FLAGS: identity, personal-data
// Password login. It writes the email into the `user` sessionStorage entry *before* the request, so a
// rejected attempt still leaves `{ email }` there for the rest of the tab.
// Verify user credentials
const login = async (
  userName,
  password,
  recaptchaToken,
  baseUrl: string | null = null,
) => {
  saveUserToStorage({ email: userName });
  const { data } = await axios.post(
    apiPaths.login,
    {
      userName,
      password,
      recaptchaToken,
    },
    {
      baseURL: baseUrl || undefined,
    },
  );

  if (data?.twoFactorVerification === "pending") {
    refreshUserSessionId();
    return data;
  }
  refreshUserSessionId();
  return getUserProfile();
};

// Verify Email Id Api call
const isEmailRegistered = async (email) => {
  const { data } = await axios.get(`${apiPaths.emailRegistered}/${email}`);
  return data;
};

interface LogoutResponse {
  federatedLogoutUrl?: string | null;
}

// FLAGS: identity, personal-data
// Sign-out. It removes the `user` entry, `retryLogin`, the expert filters, both third-party identities,
// one of the two React Query clients and the session id — and nothing else. It does not call
// `clearUserFromStorage`, so the Application Insights user id stays set; the full list of what survives
// is in MODULE.md, `## Security`, and several other MODULE.md files point at this function for it.
// Log out & Remove Local storage
export const logout = async () => {
  customerioService.clearIdentify();
  sessionStorage.removeItem("user");
  localStorage.removeItem(STORAGE_KEYS.retryLogin);
  sessionStorageService.clearExpertsFiltersFromStorage();
  posthogService.clearUser();
  queryClient.clear();

  // Direct-bearer session (OI-25014): sign-out drops the stored tokens and ends
  // the Keycloak SSO session (otherwise the next login silently rides the realm
  // cookie and skips authentication entirely). A prior exchange-path login may
  // have left an OpenUp cookie session in this browser, so end that too — best
  // effort, before the redirect — otherwise the cookie + refresh cookie outlive
  // sign-out as a still-valid API credential.
  const directSession = getDirectSession();
  if (directSession) {
    try {
      // Session not cleared yet, so the interceptor attaches the bearer; the
      // response interceptor bypasses token refresh for the logout URL.
      await axios.post(apiPaths.logout);
    } catch {
      // ignore — an absent/expired cookie session is fine; sign-out proceeds
    }
    clearDirectSession();
    sessionStorageService.clearUserSessionIdFromStorage();
    const endSessionUrl = getPhaseTwoEndSessionUrl(directSession.idToken);
    if (endSessionUrl) {
      window.location.href = endSessionUrl;
    }
    return;
  }

  let federatedLogoutUrl: string | null | undefined;
  try {
    // No body on purpose: the WAF blocks request bodies containing URLs, so the
    // API derives the post-logout redirect from the Origin header instead.
    const { data } = await axios.post<LogoutResponse>(apiPaths.logout);
    federatedLogoutUrl = data?.federatedLogoutUrl;
  } catch {
    // ignore — local cleanup still proceeds
  }

  sessionStorageService.clearUserSessionIdFromStorage();

  if (federatedLogoutUrl) {
    window.location.href = federatedLogoutUrl;
  }
};

export const useAppSelector: TypedUseSelectorHook<RootState> = useSelector;
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const useAppDispatch = () => useDispatch<any>();
interface UseAuthenticationResult {
  isLoggedIn: boolean;
  isLoggedInOrTwoFactorPending: boolean;
  user: UserType | null;
}

export const useAuthentication = (): UseAuthenticationResult => {
  const isLoggedIn = useAppSelector((state) =>
    Boolean(state.authReducers?.isLoggedIn),
  );

  const isLoggedInOrTwoFactorPending = useAppSelector((state) => {
    const user = state.authReducers?.user;
    const hasPending2FA =
      user &&
      typeof user === "object" &&
      user !== null &&
      "twoFactorVerification" in user &&
      user.twoFactorVerification === "pending";

    return !!state.authReducers?.isLoggedIn || !!hasPending2FA;
  });

  const user = useAppSelector((state) => state.authReducers?.user);

  // FLAGS: consent
  // The one place a fresh sign-in and a restored session converge, which is why the classification signal
  // fires here rather than in a login form. Guarded on `isLoggedIn` alone, never on
  // `isLoggedInOrTwoFactorPending` — that flag is true mid-2FA and would label a half-authenticated user
  // as signed in. This hook has many consumers, so the effect runs once per mounted consumer; the
  // once-per-visit guard lives in `sessionStorage` inside gtm-login-signal.ts, which is what makes the
  // repeated calls idempotent. A `useRef` here would not, and neither would a module-level flag.
  useEffect(() => {
    if (isLoggedIn) {
      signalLoginSuccess();
    }
  }, [isLoggedIn]);

  return {
    isLoggedIn,
    isLoggedInOrTwoFactorPending,
    user,
  };
};

const service = {
  registerUser,
  updateUser,
  setRetryLoginToStorage,
  getShouldRetryLoginFromStorage,
  setFocusThemes,
  registerGuestUser,
  registerBookingUser,
  getGuestUser,
  resendVerificationEmail,
  login,
  logout,
  verifyUserAccount,
  sendForgotPasswordAccessMail,
  updateUserPassword,
  getExternalUser,
  getExternalUserCodeFlow,
  isEmailRegistered,
  getUserFromStorage,
  saveUserToStorage,
  clearUserFromStorage,
  refreshAccessToken,
  signUpExternalUser,
  signUpExternalUserCodeFlow,
  getEmployerSso,
};

export default service;
